import type { ReactNode } from 'react'
import React from 'react'
import { useGroupsContext } from '@fs/zion-tree-groups'
import { useTranslation } from 'react-i18next'
import { useParams } from '@fs/zion-router'
import { Title } from '@fs/zion-head'
import { usePreference } from '@fs/zion-preferences'
import { getNameInPreferredScript } from '@fs/zion-person-service'
import ResearchHelpProvider from '../person-page/contexts/ResearchHelpContext'
import FocusFamilyMembersProvider from '../person-page/contexts/FocusFamilyMembersContext'
import useFocusPerson from '../data-service/useFocusPerson'
import LargeLoadingSpinner from '../common/LargeLoadingSpinner'
import FocusPIDProvider from './FocusPIDContext'
import PersonNotesProvider from './NotesProvider'
import ExpandedSectionsProvider from './ExpandedSectionsProvider'
import RecordHintLevelPrefProvider from './RecordHintLevelPrefProvider'
import useSwitchTree from '../hooks/useSwitchTree'
// exported just for test
export function PersonAppProvidersCore({ children }: { children: ReactNode }): JSX.Element {
  const [t] = useTranslation()
  const { focusPerson, isLoading, notFound } = useFocusPerson()
  const showNameInRomanScriptPreference = usePreference<[string], boolean>('tree.showNamesInRomanScript')
  const { fullName } = getNameInPreferredScript(focusPerson, showNameInRomanScriptPreference)
  const { selectTree, currentTreeId, isResearcherGroup } = useGroupsContext()
  const { subPage } = useParams()
  const unknownLabel = t('tree-person.unknown.label')

  const personIsInAResearcherGroup = focusPerson?.researcherTree
  const personTreeId = focusPerson?.treeId

  useSwitchTree({ focusPerson, selectTree, currentTreeId, isResearcherGroup })

  if (isLoading) return <LargeLoadingSpinner />

  return (
    <>
      {!isLoading && (focusPerson || notFound) && (
        <Title>
          {t('tree-person.details.browser.title', {
            personName: fullName ?? unknownLabel,
            lifespan: focusPerson?.lifespan ?? unknownLabel,
            subPage,
          })}
        </Title>
      )}
      {focusPerson && !focusPerson.deleted ? (
        <PersonNotesProvider>
          <FocusFamilyMembersProvider>
            <RecordHintLevelPrefProvider>
              <ResearchHelpProvider isResearcherGroup={personIsInAResearcherGroup} treeId={personTreeId}>
                {children}
              </ResearchHelpProvider>
            </RecordHintLevelPrefProvider>
          </FocusFamilyMembersProvider>
        </PersonNotesProvider>
      ) : (
        children
      )}
    </>
  )
}

export default function PersonAppProviders({ children }: { children: ReactNode }): JSX.Element {
  return (
    <FocusPIDProvider>
      <ExpandedSectionsProvider>
        <PersonAppProvidersCore>{children}</PersonAppProvidersCore>
      </ExpandedSectionsProvider>
    </FocusPIDProvider>
  )
}
